import { toLower, trim } from 'lodash-es';
import type { ArgumentAction } from 'redux-logic/definitions/action';

import type { ClockFormat } from '__generated__/graphql';

import type { PasscodeState } from './passcode.types';
import type { ActiveStudySite, AuthUser, UserSecurityQuestions } from './types';

export interface LogoutRequestOptions {
    endOktaSessionLocally?: boolean;
}

export interface SetActiveUserFromHandoffPayload {
    user: AuthUser;
    activeStudySite: ActiveStudySite;
}

export enum AuthActionType {
    CHANGE_PASS = 'CHANGE_PASS',
    CHANGE_PASS_SUCCESS = 'CHANGE_PASS_SUCCESS',
    CHANGE_PASS_FAILURE = 'CHANGE_PASS_FAILURE',
    CREATE_PASSWORD = 'CREATE_PASSWORD',
    CREATE_PASS_SUCCESS = 'CREATE_PASS_SUCCESS',
    CREATE_PASS_FAILURE = 'CREATE_PASS_FAILURE',
    LOGIN_REQUEST = 'LOGIN_REQUEST',
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    LOGIN_FAILURE = 'LOGIN_FAILURE',
    LOGOUT_REQUEST = 'LOGOUT_REQUEST',
    LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
    LOG_OUT_PRESERVING_DEVICE_HANDOFF_STATE = 'LOG_OUT_PRESERVING_DEVICE_HANDOFF_STATE',
    SET_ACTIVE_USER_FROM_HANDOFF = 'SET_ACTIVE_USER_FROM_HANDOFF',
    RESET_USER_DATA = 'RESET_USER_DATA',
    POLICY_REQUEST = 'POLICY_REQUEST',
    POLICY_SUCCESS = 'POLICY_SUCCESS',
    POLICY_FAILURE = 'POLICY_FAILURE',
    CLEAR_ERROR = 'CLEAR_ERROR',
    UPDATE_ACTIVE_STUDY_SITE = 'UPDATE_ACTIVE_STUDY_SITE',
    UPDATE_ACTIVE_USER_PASSCODE_STATE = 'UPDATE_ACTIVE_USER_PASSCODE_STATE',
    RESET_ACTIVE_STUDY_SITE = 'RESET_ACTIVE_STUDY_SITE',
    RESET_COMPLETE = 'RESET_COMPLETE',
    HANDLE_PASSWORD_CHANGE_STATUS = 'HANDLE_PASSWORD_CHANGE_STATUS',
    USER_CHANGE_PASS = 'USER_CHANGE_PASS',
    USER_CHANGE_PASS_SUCCESS = 'USER_CHANGE_PASS_SUCCESS',
    USER_CHANGE_PASS_FAILURE = 'USER_CHANGE_PASS_FAILURE',
    USER_UPDATE_SECURITY_QUESTIONS = 'USER_UPDATE_SECURITY_QUESTIONS',
    TELEVISIT_REQUEST = 'TELEVISIT_REQUEST',
    TELEVISIT_THUMBNAIL_MODE = 'TELEVISIT_THUMBNAIL_MODE',
    UPDATE_PATIENT_STUDY_ID = 'UPDATE_PATIENT_STUDY_ID',
    UPDATE_PATIENT_ARM_ID = 'UPDATE_PATIENT_ARM_ID',
    UPDATE_PATIENT_CLOCK_FORMAT = 'UPDATE_PATIENT_CLOCK_FORMAT',
    ACTIVATION_PASSWORD_REQUEST = 'ACTIVATION_PASSWORD_REQUEST',
    SET_PASSWORD_RESET_USER_ID = 'SET_PASSWORD_RESET_USER_ID',
    SET_SSO_LOADING = 'SET_SSO_LOADING',
    USER_ROLE_SWITCH_PENDING = 'USER_ROLE_SWITCH_PENDING',
    FORGOT_CREDENTIALS_QUESTIONS_FOUND = 'FORGOT_CREDENTIALS_QUESTIONS_FOUND',
    FORGOT_CREDENTIALS_QUESTIONS_LOADED_FOR_RESET_LINK = 'FORGOT_CREDENTIALS_QUESTIONS_LOADED_FOR_RESET_LINK',
    FORGOT_CREDENTIALS_ACTIVATION_NEEDED = 'FORGOT_CREDENTIALS_ACTIVATION_NEEDED',
    FORGOT_CREDENTIALS_ACTIVATION_CODE_ISSUED = 'FORGOT_CREDENTIALS_ACTIVATION_CODE_ISSUED',
    FORGOT_CREDENTIALS_QUESTIONS_DISCARDED = 'FORGOT_CREDENTIALS_QUESTIONS_DISCARDED',
    FORGOT_CREDENTIALS_CLEARED = 'FORGOT_CREDENTIALS_CLEARED',
}

export const AuthActions = {
    forgotCredentialsQuestionsFound: (
        username: string,
        securityQuestions: UserSecurityQuestions
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_FOUND,
        payload: { username, securityQuestions },
    }),
    forgotCredentialsQuestionsLoadedForResetLink: (
        securityQuestions: UserSecurityQuestions
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_LOADED_FOR_RESET_LINK,
        payload: { securityQuestions },
    }),
    forgotCredentialsActivationNeeded: (username: string): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FORGOT_CREDENTIALS_ACTIVATION_NEEDED,
        payload: { username },
    }),
    forgotCredentialsActivationCodeIssued: (activationCode: string): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FORGOT_CREDENTIALS_ACTIVATION_CODE_ISSUED,
        payload: { activationCode },
    }),
    /** Drops the answered questions but keeps the activation code the answers produced. */
    forgotCredentialsQuestionsDiscarded: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_DISCARDED,
    }),
    forgotCredentialsCleared: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FORGOT_CREDENTIALS_CLEARED,
    }),
    changePasswordFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CHANGE_PASS_FAILURE,
        payload: err,
        error: true,
    }),
    createPasswordSucceeded: (authUser, newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CREATE_PASS_SUCCESS,
        payload: { authUser, newPassword },
    }),
    createPasswordFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CREATE_PASS_FAILURE,
        payload: err,
        error: true,
    }),
    loginRequest: (username, password, ssoTokens?): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGIN_REQUEST,
        payload: { username: toLower(trim(username)), password: trim(password), ssoTokens },
    }),
    loginSucceeded: (authUser: AuthUser): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGIN_SUCCESS,
        payload: authUser,
    }),
    loginFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGIN_FAILURE,
        payload: err,
        error: true,
    }),
    logoutRequest: (options?: LogoutRequestOptions): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGOUT_REQUEST,
        payload: options,
    }),
    logoutSucceeded: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGOUT_SUCCESS,
    }),
    logOutPreservingDeviceHandoffState: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOG_OUT_PRESERVING_DEVICE_HANDOFF_STATE,
    }),
    setActiveUserFromHandoff: (
        user: AuthUser,
        activeStudySite: ActiveStudySite
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.SET_ACTIVE_USER_FROM_HANDOFF,
        payload: { user, activeStudySite },
    }),
    resetUserData: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.RESET_USER_DATA,
    }),
    policyRequest: (userData): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.POLICY_REQUEST,
        payload: userData,
    }),
    policySucceeded: (userData): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.POLICY_SUCCESS,
        payload: userData,
    }),
    policyFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.POLICY_FAILURE,
        payload: err,
        error: true,
    }),
    updateActiveStudySite: (payload: {
        study?;
        site?;
        updatedUser?;
    }): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_ACTIVE_STUDY_SITE,
        payload,
    }),
    updateActiveUserPasscodeState: (payload: PasscodeState): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_ACTIVE_USER_PASSCODE_STATE,
        payload,
    }),
    resetActiveStudySite: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.RESET_ACTIVE_STUDY_SITE,
    }),
    resetComplete: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.RESET_COMPLETE,
    }),
    userChangePasswordRequest: (oldPassword, newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_CHANGE_PASS,
        payload: { oldPassword: trim(oldPassword), newPassword: trim(newPassword) },
    }),
    userChangePasswordSucceeded: (
        authUser: AuthUser,
        oldPassword: string,
        newPassword: string
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_CHANGE_PASS_SUCCESS,
        payload: {
            authUser,
            oldPassword,
            newPassword,
        },
    }),
    userChangePasswordFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_CHANGE_PASS_FAILURE,
        payload: err,
        error: true,
    }),
    userUpdateSecurityQuestions: (questions): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_UPDATE_SECURITY_QUESTIONS,
        payload: questions,
    }),
    updateTelevisitStatus: (data, active: boolean = false): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.TELEVISIT_REQUEST,
        payload: {
            data,
            active,
        },
    }),
    updateTelevisitThumbnailMode: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.TELEVISIT_THUMBNAIL_MODE,
    }),
    updatePatientStudyId: (data): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_PATIENT_STUDY_ID,
        payload: {
            data,
        },
    }),
    updatePatientArmId: (data): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_PATIENT_ARM_ID,
        payload: {
            data,
        },
    }),
    updatePatientClockFormat: (data: ClockFormat | undefined): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_PATIENT_CLOCK_FORMAT,
        payload: {
            data,
        },
    }),
    isNewPasswordRequested: (data): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.ACTIVATION_PASSWORD_REQUEST,
        payload: {
            data,
        },
    }),
    setPasswordResetUserId: (userId: string | null): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.SET_PASSWORD_RESET_USER_ID,
        payload: { userId },
    }),
    setSSOLoading: (data: boolean): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.SET_SSO_LOADING,
        payload: {
            data,
        },
    }),
    updateUserRoleSwitchPending: (isPending: boolean): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_ROLE_SWITCH_PENDING,
        payload: {
            isPending,
        },
    }),
    // Used from connect in update_password_form.tsx
    changePasswordRequest: (newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CHANGE_PASS,
        payload: { newPassword: trim(newPassword) },
    }),
    // Used from connect in create_password_form.tsx
    createPasswordRequest: (newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CREATE_PASSWORD,
        payload: { newPassword: trim(newPassword) },
    }),
    changePasswordSucceeded: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CHANGE_PASS_SUCCESS,
    }),
    clearError: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CLEAR_ERROR,
        error: false,
    }),
    // Used in patientApp/general.tsx and in user/general.tsx
    handlePasswordChangeStatus: (status): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.HANDLE_PASSWORD_CHANGE_STATUS,
        payload: status,
    }),
};
